/**
 * TeamPage — Team workspaces (V2 additive).
 *
 * The team admin/member hub at /team, scoped to the ACTIVE team workspace:
 *   Members : list + roles, invite by email, pending invites.
 *   Usage   : pooled-credit consumption per member and per day (from the
 *             org-owned Orders rows).
 *   Billing : plan summary, seat / credit-tier changes (admins, prorated via
 *             Stripe), shared-pool top-ups (ANY member, min 500 credits),
 *             Stripe billing portal.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, UserPlus, Trash2, Loader2, CreditCard, BarChart3,
  Crown, ShieldCheck, Mail, Minus, Plus, Zap, LogOut,
} from 'lucide-react';
import { cn } from '@/v2/utils';
import { useUserStore } from '@/store/userStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { orgService, type OrgDetails, type OrgInvite, type OrgUsage } from '@/api/orgService';
import { TEAM_PLAN } from '@/api/config';
import { CreateTeamModal } from '@/v2/components/CreateTeamModal';
import { toast } from '@/v2/toast';

type Tab = 'members' | 'usage' | 'billing';

const CREDIT_PACK_PRICE_PER_10 = 2; // $2 / 10 credits — same rate as personal top-ups

function RoleBadge({ role }: { role: string }) {
  const { t } = useTranslation('v2');
  const cls =
    role === 'owner'
      ? 'bg-amber-400/15 text-amber-300 border-amber-400/30'
      : role === 'admin'
        ? 'bg-primary/15 text-primary border-primary/30'
        : 'bg-white/5 text-text-secondary border-glass-border';
  const Icon = role === 'owner' ? Crown : role === 'admin' ? ShieldCheck : Users;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border', cls)}>
      <Icon className="w-3 h-3" /> {t(`team.role_${role}`)}
    </span>
  );
}

export function TeamPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useUserStore((s) => s.user);
  const userId = useUserStore((s) => s.userId);
  const refreshUser = useUserStore((s) => s.refreshUser);
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId);
  const setActiveOrg = useWorkspaceStore((s) => s.setActiveOrg);

  const orgs = useMemo(() => (user?.organizations || []).filter((o) => o.orgStatus !== 'expired'), [user]);
  // Prefer the active workspace's org; else the first org the user belongs to.
  const orgSummary = orgs.find((o) => o.orgId === activeOrgId) || orgs[0] || null;

  const [tab, setTab] = useState<Tab>((searchParams.get('tab') as Tab) || 'members');
  const [details, setDetails] = useState<OrgDetails | null>(null);
  const [invites, setInvites] = useState<OrgInvite[]>([]);
  const [usage, setUsage] = useState<OrgUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [inviting, setInviting] = useState(false);

  // Billing controls
  const [topUpAmount, setTopUpAmount] = useState<number>(TEAM_PLAN.topUpMinCredits);

  const role = orgSummary?.role || 'member';
  const isAdmin = role === 'owner' || role === 'admin';
  const orgId = orgSummary?.orgId;

  const loadAll = useCallback(async () => {
    if (!userId || !orgId) return;
    setLoading(true);
    try {
      const d = await orgService.getOrg(userId, orgId);
      setDetails(d);
      if (isAdmin) {
        setInvites(await orgService.listInvites(userId, orgId));
      }
    } catch {
      // membership may have just been revoked — refresh user info to reconcile
      void refreshUser();
    } finally {
      setLoading(false);
    }
  }, [userId, orgId, isAdmin, refreshUser]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!userId || !orgId || tab !== 'usage') return;
    orgService.getUsage(userId, orgId, 30).then(setUsage).catch(() => setUsage(null));
  }, [userId, orgId, tab]);

  // Stripe return: ?checkout=success → the webhook activates the org; refresh.
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!checkout) return;
    if (checkout === 'success') {
      toast.success(t('team.checkoutSuccess'));
      void refreshUser();
      void loadAll();
    }
    window.history.replaceState({}, '', '/team');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- No team yet ----------
  if (!orgSummary) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-16 flex flex-col items-center text-center animate-fade-in">
        <span className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/25 to-accent/25 flex items-center justify-center mb-5">
          <Users className="w-8 h-8 text-primary" />
        </span>
        <h1 className="text-2xl font-bold text-text-primary mb-2">{t('team.emptyTitle')}</h1>
        <p className="text-sm text-text-secondary max-w-md mb-6">{t('team.emptyDescription')}</p>
        <button
          onClick={() => setCreateOpen(true)}
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-white font-semibold"
        >
          {t('team.createTeam')}
        </button>
        {createOpen && <CreateTeamModal onClose={() => setCreateOpen(false)} />}
      </div>
    );
  }

  const members = details?.members || [];
  const memberCount = details?.memberCount || members.length;
  const seatsTotal = details?.seats ?? orgSummary.seats;
  const pool = details?.remainingCredit ?? orgSummary.remainingCredit;
  const weeklyCredits = details?.weeklyCredits ?? orgSummary.weeklyCredits;
  const billingPeriod = details?.billingPeriod || orgSummary.billingPeriod || 'weekly';
  const isPending = (details?.orgStatus || orgSummary.orgStatus) === 'pending';

  const handleInvite = async () => {
    if (!userId || !orgId || !inviteEmail.trim() || inviting) return;
    setInviting(true);
    try {
      const inv = await orgService.sendInvite(userId, orgId, inviteEmail.trim(), inviteRole);
      setInvites((prev) => [inv, ...prev]);
      setInviteEmail('');
      toast.success(t('team.inviteSent', { email: inv.email }));
    } catch (e) {
      const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (code === 'no_seats_available') toast.error(t('team.noSeatsAvailable'));
      else if (code === 'already_invited') toast.error(t('team.alreadyInvited'));
      else toast.error(t('team.inviteError'));
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberUserId: string) => {
    if (!userId || !orgId) return;
    try {
      await orgService.removeMember(userId, orgId, memberUserId);
      setDetails((d) => d ? { ...d, members: d.members.filter((m) => m.user_id !== memberUserId), memberCount: d.memberCount - 1 } : d);
    } catch {
      toast.error(t('team.genericError'));
    }
  };

  const handleRoleChange = async (memberUserId: string, newRole: 'admin' | 'member') => {
    if (!userId || !orgId) return;
    try {
      await orgService.setMemberRole(userId, orgId, memberUserId, newRole);
      setDetails((d) => d ? {
        ...d,
        members: d.members.map((m) => m.user_id === memberUserId ? { ...m, role: newRole } : m),
      } : d);
    } catch {
      toast.error(t('team.genericError'));
    }
  };

  const handleRevoke = async (token: string) => {
    if (!userId || !orgId) return;
    try {
      await orgService.revokeInvite(userId, orgId, token);
      setInvites((prev) => prev.filter((i) => i.invite_token !== token));
    } catch {
      toast.error(t('team.genericError'));
    }
  };

  const handleLeave = async () => {
    if (!userId || !orgId) return;
    try {
      await orgService.leaveOrg(userId, orgId);
      setActiveOrg(null);
      void refreshUser();
      navigate('/');
    } catch {
      toast.error(t('team.genericError'));
    }
  };

  const handleTopUp = async () => {
    if (!userId || !orgId) return;
    const amount = Math.max(TEAM_PLAN.topUpMinCredits, Math.round(topUpAmount / 10) * 10);
    try {
      const session = await orgService.teamTopUp({
        userId, orgId, creditAmount: amount,
        successUrl: `${window.location.origin}/team?checkout=success&tab=billing`,
        cancelUrl: `${window.location.origin}/team?tab=billing`,
      });
      if (session.checkout_url) window.location.href = session.checkout_url;
      else toast.error(t('team.checkoutError'));
    } catch {
      toast.error(t('team.checkoutError'));
    }
  };

  const topUpPrice = (Math.max(TEAM_PLAN.topUpMinCredits, topUpAmount) / 10) * CREDIT_PACK_PRICE_PER_10;
  const maxByDay = Math.max(1, ...(usage?.by_day || []).map((d) => d.credits));
  const maxByMember = Math.max(1, ...(usage?.by_member || []).map((m) => m.credits));

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-12">
      <div className="max-w-4xl mx-auto animate-fade-in">
        {/* Header */}
        <div className="pt-8 pb-6 flex flex-wrap items-center gap-4">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/25 to-accent/25 flex items-center justify-center">
            <Users className="w-6 h-6 text-primary" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold text-text-primary truncate">{orgSummary.name}</h1>
              <RoleBadge role={role} />
            </div>
            <p className="text-sm text-text-tertiary">
              {t('team.headerSummary', { members: memberCount, seats: seatsTotal })}
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface border border-primary/30">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm text-text-secondary">{t('team.teamCredits')}</span>
            <span className="text-sm font-bold text-text-primary">{Math.floor(pool)}</span>
            <span className="text-xs text-text-tertiary">/ {weeklyCredits}{t('team.perWeekShort')}</span>
          </div>
        </div>

        {isPending && (
          <div className="mb-6 p-4 rounded-2xl border border-amber-400/40 bg-amber-500/10 flex flex-wrap items-center gap-3">
            <p className="flex-1 text-sm text-text-primary">{t('team.pendingBanner')}</p>
            <button
              onClick={() => setResumeOpen(true)}
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors"
            >
              {t('team.finishSetup')}
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1.5 p-1 rounded-xl bg-surface-light w-fit mb-6">
          {([
            ['members', UserPlus, t('team.tabMembers')],
            ['usage', BarChart3, t('team.tabUsage')],
            ['billing', CreditCard, t('team.tabBilling')],
          ] as const).map(([key, Icon, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                tab === key ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'
              )}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {loading && !details ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : tab === 'members' ? (
          <div className="space-y-6">
            {isAdmin && (
              <div className="p-4 rounded-2xl bg-surface border border-glass-border">
                <p className="text-sm font-semibold text-text-primary mb-3">{t('team.inviteTitle')}</p>
                <div className="flex flex-wrap gap-2">
                  <input
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleInvite(); }}
                    placeholder={t('team.inviteEmailPlaceholder')}
                    type="email"
                    className="flex-1 min-w-[200px] px-4 py-2.5 rounded-xl bg-surface-light border border-glass-border focus:border-primary/60 focus:outline-none text-sm text-text-primary placeholder:text-text-tertiary"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin')}
                    className="px-3 py-2.5 rounded-xl bg-surface-light border border-glass-border text-sm text-text-primary focus:outline-none"
                  >
                    <option value="member">{t('team.role_member')}</option>
                    <option value="admin">{t('team.role_admin')}</option>
                  </select>
                  <button
                    onClick={handleInvite}
                    disabled={!inviteEmail.trim() || inviting}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold disabled:opacity-40"
                  >
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    {t('team.sendInvite')}
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-text-tertiary">
                  {t('team.seatsUsage', { used: memberCount + invites.length, total: seatsTotal })}
                </p>
              </div>
            )}

            <div className="rounded-2xl bg-surface border border-glass-border divide-y divide-glass-border">
              {members.map((m) => (
                <div key={m.user_id} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center text-sm font-bold text-primary-light shrink-0">
                    {(m.display_name || m.email || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {m.display_name || m.email}
                      {m.user_id === userId && <span className="ml-1.5 text-xs text-text-tertiary">{t('team.you')}</span>}
                    </p>
                    <p className="text-xs text-text-tertiary truncate">{m.email}</p>
                  </div>
                  {role === 'owner' && m.role !== 'owner' ? (
                    <select
                      value={m.role}
                      onChange={(e) => void handleRoleChange(m.user_id, e.target.value as 'admin' | 'member')}
                      className="px-2 py-1 rounded-lg bg-surface-light border border-glass-border text-xs text-text-primary focus:outline-none"
                    >
                      <option value="member">{t('team.role_member')}</option>
                      <option value="admin">{t('team.role_admin')}</option>
                    </select>
                  ) : (
                    <RoleBadge role={m.role} />
                  )}
                  {isAdmin && m.role !== 'owner' && m.user_id !== userId && (
                    <button
                      onClick={() => void handleRemove(m.user_id)}
                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-text-tertiary hover:text-red-400 transition-colors"
                      aria-label={t('team.removeMember')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {isAdmin && invites.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-text-primary mb-2">{t('team.pendingInvites')}</p>
                <div className="rounded-2xl bg-surface border border-glass-border divide-y divide-glass-border">
                  {invites.map((inv) => (
                    <div key={inv.invite_token} className="flex items-center gap-3 px-4 py-3">
                      <Mail className="w-4 h-4 text-text-tertiary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-primary truncate">{inv.email}</p>
                        <p className="text-xs text-text-tertiary">{t(`team.role_${inv.role}`)}</p>
                      </div>
                      <button
                        onClick={() => void handleRevoke(inv.invite_token)}
                        className="text-xs text-text-tertiary hover:text-red-400 transition-colors"
                      >
                        {t('team.revoke')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {role !== 'owner' && (
              <button
                onClick={handleLeave}
                className="flex items-center gap-1.5 text-xs text-text-tertiary hover:text-red-400 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" /> {t('team.leaveTeam')}
              </button>
            )}
          </div>
        ) : tab === 'usage' ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                [t('team.usageCreditsUsed'), Math.round(usage?.total_credits ?? 0)],
                [t('team.usageVideos'), usage?.total_orders ?? 0],
                [t('team.usageRemaining'), Math.floor(usage?.remaining_credit ?? pool)],
              ].map(([label, value]) => (
                <div key={String(label)} className="p-4 rounded-2xl bg-surface border border-glass-border">
                  <p className="text-xs text-text-tertiary">{label}</p>
                  <p className="text-2xl font-extrabold text-text-primary mt-1">{value}</p>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-2xl bg-surface border border-glass-border">
              <p className="text-sm font-semibold text-text-primary mb-3">{t('team.usageByMember')}</p>
              {(usage?.by_member || []).length === 0 ? (
                <p className="text-sm text-text-tertiary">{t('team.usageEmpty')}</p>
              ) : (
                <div className="space-y-2.5">
                  {(usage?.by_member || []).map((m) => (
                    <div key={m.user_id}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-text-secondary truncate">{m.display_name || m.user_id.slice(0, 8)}</span>
                        <span className="text-text-tertiary">{t('team.usageCreditsCount', { count: Math.round(m.credits) })}</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary to-accent"
                          style={{ width: `${Math.max(3, (m.credits / maxByMember) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(usage?.by_day || []).length > 0 && (
              <div className="p-4 rounded-2xl bg-surface border border-glass-border">
                <p className="text-sm font-semibold text-text-primary mb-3">{t('team.usageLast30Days')}</p>
                <div className="flex items-end gap-1 h-24">
                  {(usage?.by_day || []).map((d) => (
                    <div
                      key={d.day}
                      title={`${d.day}: ${Math.round(d.credits)}`}
                      className="flex-1 rounded-t bg-primary/60 hover:bg-primary transition-colors"
                      style={{ height: `${Math.max(4, (d.credits / maxByDay) * 100)}%` }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Plan summary */}
            <div className="p-5 rounded-2xl bg-surface border border-glass-border">
              <div className="mb-4">
                <p className="text-sm font-semibold text-text-primary">{t('team.planTitle')}</p>
                <p className="text-xs text-text-tertiary">
                  {billingPeriod === 'yearly' ? t('team.billedYearly') : t('team.billedWeekly')}
                  {' · '}{t('team.planSummary', { seats: seatsTotal, credits: weeklyCredits })}
                </p>
              </div>

              {isAdmin && !isPending && (
                <div className="p-4 rounded-xl bg-surface-light border border-glass-border">
                  <p className="text-sm font-medium text-text-primary">{t('team.planContactTitle')}</p>
                  <p className="text-xs text-text-tertiary mt-1 mb-3">{t('team.planContactBody')}</p>
                  <a
                    href="mailto:support@vivideo.ai"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-surface text-sm font-semibold text-text-primary hover:text-primary border border-glass-border transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    {t('team.contactSupport')}
                  </a>
                </div>
              )}
            </div>

            {/* Shared-pool top-up — ANY member */}
            {!isPending && (
              <div className="p-6 rounded-2xl bg-surface border border-primary/30">
                <p className="text-lg font-bold text-text-primary">{t('team.topUpTitle')}</p>
                <p className="text-sm text-text-tertiary mt-1 mb-5">
                  {t('team.topUpSubtitle', { min: TEAM_PLAN.topUpMinCredits })}
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setTopUpAmount((v) => Math.max(TEAM_PLAN.topUpMinCredits, v - 100))}
                      className="w-11 h-11 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center"
                      aria-label={t('team.lessCredits')}
                    >
                      <Minus className="w-5 h-5 text-text-secondary" />
                    </button>
                    <span className="w-20 text-center text-2xl font-extrabold text-text-primary">{topUpAmount}</span>
                    <button
                      onClick={() => setTopUpAmount((v) => Math.min(10000, v + 100))}
                      className="w-11 h-11 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center"
                      aria-label={t('team.moreCredits')}
                    >
                      <Plus className="w-5 h-5 text-text-secondary" />
                    </button>
                  </div>
                  <button
                    onClick={handleTopUp}
                    className="flex-1 min-w-[200px] py-3.5 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-base font-bold"
                  >
                    {t('team.buyCreditsFor', { price: topUpPrice.toFixed(2) })}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {resumeOpen && orgId && (
          <CreateTeamModal
            onClose={() => setResumeOpen(false)}
            resumeOrg={{ orgId, name: orgSummary.name }}
          />
        )}
      </div>
    </div>
  );
}
